import React from 'react';

interface SearchFilters {
  keyword?: string;
  skill_ids?: number[];
  occupation_ids?: number[];
  industry_ids?: number[];
  characteristic_ids?: number[];
  prefecture_ids?: number[];
  workstyle_types?: string[];
  availability_types?: string[];
  price_over?: number;
  payment_site?: string;
  settlement_hours?: string;
  interview_count_max?: number;
  published_within?: number;
  is_recruiting?: boolean;
  remote_types?: string[];
  workday_types?: string[];
  job_source_ids?: number[];
  sort?: string;
  page?: number;
}

interface SavedSearchCondition {
  id: number;
  search_params: SearchFilters;
  is_active: boolean;
  created_at: string;
}

interface SavedSearchConditionCardProps {
  condition: SavedSearchCondition;
  onToggle: (id: number, isActive: boolean) => void;
  onDelete: (id: number) => void;
  onSearch: (searchParams: SearchFilters) => void;
}

const SavedSearchConditionCard: React.FC<SavedSearchConditionCardProps> = ({
  condition,
  onToggle,
  onDelete,
  onSearch
}) => {
  // 検索条件の概要を生成
  const generateSummary = (): string => {
    const parts: string[] = [];

    if (condition.search_params.keyword) {
      parts.push(`キーワード: ${condition.search_params.keyword}`);
    }

    if (condition.search_params.skill_ids && condition.search_params.skill_ids.length > 0) {
      parts.push(`スキル: ${condition.search_params.skill_ids.length}件`);
    }

    if (condition.search_params.occupation_ids && condition.search_params.occupation_ids.length > 0) {
      parts.push(`職種: ${condition.search_params.occupation_ids.length}件`);
    }

    if (condition.search_params.prefecture_ids && condition.search_params.prefecture_ids.length > 0) {
      parts.push(`都道府県: ${condition.search_params.prefecture_ids.length}件`);
    }

    if (condition.search_params.price_over) {
      parts.push(`単価: ${(condition.search_params.price_over / 10000).toFixed(0)}万円以上`);
    }

    if (condition.search_params.remote_types && condition.search_params.remote_types.length > 0) {
      const remoteLabels = condition.search_params.remote_types.map(type => {
        switch (type) {
          case 'remote': return 'リモートOK';
          case 'full_remote': return 'フルリモート';
          case 'onsite': return '常駐';
          default: return type;
        }
      });
      parts.push(`勤務形態: ${remoteLabels.join(', ')}`);
    }

    return parts.length > 0 ? parts.join(' / ') : '検索条件が設定されています';
  };

  // 保存日時をフォーマット
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="card shadow-sm mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="flex-grow-1">
            <p className="mb-1 text-muted small">{formatDate(condition.created_at)}</p>
            <p className="mb-0">{generateSummary()}</p>
          </div>
          <div className="d-flex align-items-center gap-2">
            {/* Toggleアイコン */}
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id={`toggle-${condition.id}`}
                checked={condition.is_active}
                onChange={() => onToggle(condition.id, condition.is_active)}
              />
              <label className="form-check-label" htmlFor={`toggle-${condition.id}`}>
                <small className="text-muted">メール受信</small>
              </label>
            </div>
          </div>
        </div>
        <div className="d-flex gap-2 mt-3">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => onSearch(condition.search_params)}
          >
            <i className="bi bi-search me-1"></i>
            この条件で検索
          </button>
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            onClick={() => onDelete(condition.id)}
          >
            <i className="bi bi-trash me-1"></i>
            削除
          </button>
        </div>
      </div>
    </div>
  );
};

export default SavedSearchConditionCard;

